const normalizeOptionText = (value) =>
	`${value ?? ""}`.trim().replace(/\s+/g, " ").toLowerCase()

const normalizeOptionTextLoose = (value) =>
	normalizeOptionText(value).replace(/\s+/g, "")

const valuesMatch = (a, b, loose = false) => {
	const normalize = loose ? normalizeOptionTextLoose : normalizeOptionText
	return normalize(a) === normalize(b)
}

const humanizeOptionName = (name, fallback) => {
	const humanized = `${name || ""}`
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/\s+name$/i, "")

	if (!humanized) return fallback

	return humanized.replace(/\b\w/g, (char) => char.toUpperCase())
}

const makeUniqueOptionName = ({ name, sourceName, usedNames, position }) => {
	const candidates = [
		name,
		humanizeOptionName(sourceName, null),
		`Option ${position}`,
	]

	for (const candidate of candidates) {
		const normalized = normalizeOptionText(candidate)
		if (candidate && !usedNames.has(normalized)) {
			usedNames.add(normalized)
			return candidate
		}
	}

	let uniqueName = `${name || "Option"} ${position}`
	let normalized = normalizeOptionText(uniqueName)
	while (usedNames.has(normalized)) {
		uniqueName = `${uniqueName} ${position}`
		normalized = normalizeOptionText(uniqueName)
	}
	usedNames.add(normalized)
	return uniqueName
}

const findOptionIndexForValue = (options = [], value, preferredIndex) => {
	if (!value) return preferredIndex

	const preferredOption = options?.[preferredIndex]
	if (
		preferredOption?.values?.some((optionValue) =>
			valuesMatch(optionValue, value),
		)
	) {
		return preferredIndex
	}

	const exactIndex = options.findIndex((option) =>
		option?.values?.some((optionValue) => valuesMatch(optionValue, value)),
	)
	if (exactIndex >= 0) return exactIndex

	if (
		preferredOption?.values?.some((optionValue) =>
			valuesMatch(optionValue, value, true),
		)
	) {
		return preferredIndex
	}

	const looseIndex = options.findIndex((option) =>
		option?.values?.some((optionValue) =>
			valuesMatch(optionValue, value, true),
		),
	)

	return looseIndex >= 0 ? looseIndex : preferredIndex
}

const translateOptionValue = ({ translatedOption, originalValue }) => {
	if (!originalValue) return originalValue

	const translatedValue =
		translatedOption?.values?.find((value) =>
			valuesMatch(value.originalName, originalValue),
		) ||
		translatedOption?.values?.find((value) =>
			valuesMatch(value.originalName, originalValue, true),
		) ||
		translatedOption?.values?.find((value) =>
			valuesMatch(value.name, originalValue),
		) ||
		translatedOption?.values?.find((value) =>
			valuesMatch(value.name, originalValue, true),
		)

	return translatedValue?.name || originalValue
}

export const buildTranslatedProductOptions = ({
	sourceOptions = [],
	translatedOptions = null,
}) => {
	const translatedNameCounts = (translatedOptions || []).reduce(
		(counts, option) => {
			const normalizedName = normalizeOptionText(option?.name)
			if (!normalizedName) return counts

			counts[normalizedName] = (counts[normalizedName] || 0) + 1
			return counts
		},
		{},
	)
	const usedNames = new Set()

	return sourceOptions.map((sourceOption, index) => {
		const translatedOption = translatedOptions?.[index]
		const translatedName = translatedOption?.name?.trim()
		const translatedNameIsDuplicate =
			translatedNameCounts[normalizeOptionText(translatedName)] > 1
		const sourceName = translatedOption?.originalName || sourceOption.name
		const preferredName = translatedNameIsDuplicate
			? humanizeOptionName(sourceName, `Option ${index + 1}`)
			: translatedName ||
				humanizeOptionName(sourceName, `Option ${index + 1}`)

		return {
			name: makeUniqueOptionName({
				name: preferredName,
				sourceName,
				usedNames,
				position: index + 1,
			}),
			values: (sourceOption.values || []).map((value) =>
				translateOptionValue({
					translatedOption,
					originalValue: value,
				}),
			),
		}
	})
}

export const translateVariantOptionsByProductOptions = ({
	variant,
	sourceOptions = [],
	translatedOptions = null,
}) => {
	const sourceValues = [variant.option1, variant.option2, variant.option3]
	const translatedValuesByOptionIndex = []

	sourceValues.forEach((value, preferredIndex) => {
		if (!value) return

		const optionIndex = findOptionIndexForValue(
			sourceOptions,
			value,
			preferredIndex,
		)

		translatedValuesByOptionIndex[optionIndex] = translateOptionValue({
			translatedOption: translatedOptions?.[optionIndex],
			originalValue: value,
		})
	})

	return {
		option1: translatedValuesByOptionIndex[0] || "",
		option2: translatedValuesByOptionIndex[1] || "",
		option3: translatedValuesByOptionIndex[2] || "",
	}
}
